import React from "react";
import { Link } from "react-router";

import { ColumnDef } from "@tanstack/react-table";

import { enrichmentStatusColumn } from "@/components/common/status-columns";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { makeTooltip } from "@/components/ui/tooltip";
import { IShowGenreData } from "@/types/show-genre";

export const useShowGenreColumns = () =>
  React.useMemo<ColumnDef<IShowGenreData>[]>(
    () => [
      { id: "id", accessorKey: "document.id", header: "ID" },
      {
        id: "name",
        header: "Name",
        minSize: 200,
        size: 280,
        accessorFn: (row) =>
          row.highlights?.filter((h: any) => h.field === "name")[0]?.snippet ??
          row.document.name,
        cell: function render({ row, getValue }: any) {
          return makeTooltip(
            <Link
              to={`/show-genres/show/${row.original.document.id}`}
              className="block truncate font-medium hover:underline"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            />,
          );
        },
        enableSorting: true,
      },
      {
        id: "slug",
        header: "Slug",
        size: 200,
        accessorFn: (row) => row.document.slug ?? "—",
        enableSorting: false,
        enableColumnFilter: false,
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
      },
      // No `status` column on /show-genres, so the enrichment column sits in the
      // equivalent early-middle slot — after `slug` — rather than last before
      // `actions`. show_genre is NOT embeddable: no embedding column.
      enrichmentStatusColumn<IShowGenreData>(),
      {
        id: "sections",
        header: "Sections",
        accessorFn: (row) => (row.document.sections ?? []).join(", ") || "—",
        enableSorting: false,
        enableColumnFilter: false,
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enablePinning: true,
        size: 100,
        cell: function render({ row }) {
          return (
            <div className="flex items-center gap-1">
              <ShowButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
              <EditButton
                size="sm"
                hideText={true}
                recordItemId={row.original.document.id}
              />
            </div>
          );
        },
      },
    ],
    [],
  );
